import React, { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Painel de indicadores do módulo Relatórios/BI — ocupação, evasão e saúde
 * financeira.
 *
 * O módulo é vendido como "análise de dados avançada: taxas de ocupação, evasão
 * e saúde financeira em tempo real", e até aqui a tela só despejava listagens
 * por módulo: nenhum dos três indicadores existia.
 */

interface TurmaOcupacao {
  id_turma: number; turma: string; capacidade: number | null;
  matriculadas: number; taxaPct: number | null; excedida: boolean;
}
interface Indicadores {
  periodo: { inicio: string; fim: string };
  ocupacao: {
    turmas: TurmaOcupacao[]; capacidadeTotal: number;
    matriculadasTotal: number; taxaPct: number | null; semCapacidade: string[]; semTurmaValida: number;
  };
  evasao: {
    saidas: number; cancelamentos: number; trancamentos: number;
    matriculadasNoInicio: number; taxaPct: number | null;
  };
  financeiro?: { recebidoMes?: number; aReceber?: number; vencidoValor?: number; vencidoQtd?: number; inadimplenciaPct?: number };
}

const brl = (v: number | undefined) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Verde acima de 85% de ocupação, âmbar acima de 60%, vermelho abaixo. */
const corOcupacao = (p: number) => (p > 100 ? '#c62828' : p >= 85 ? '#2e7d32' : p >= 60 ? '#e65100' : '#c62828');
/** Na evasão a escala se inverte: quanto MENOR, melhor. */
const corEvasao = (p: number) => (p <= 5 ? '#2e7d32' : p <= 15 ? '#e65100' : '#c62828');

function Cartao({ titulo, valor, cor, nota }: { titulo: string; valor: string; cor?: string; nota?: string }) {
  return (
    <div style={{ flex: '1 1 200px', background: '#fff', border: '1px solid #eee', borderLeft: `4px solid ${cor || '#1565c0'}`, borderRadius: 10, padding: 14 }}>
      <div style={{ color: '#888', fontSize: 13 }}>{titulo}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: cor || '#333' }}>{valor}</div>
      {nota && <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>{nota}</div>}
    </div>
  );
}

export default function PainelIndicadores() {
  const [dados, setDados] = useState<Indicadores | null>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    api.get<Indicadores>('/relatorios/indicadores')
      .then(({ data }) => setDados(data))
      .catch((e: any) => setErro(e?.response?.data?.message || 'Não foi possível carregar os indicadores.'))
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) return <div style={{ color: '#888', marginBottom: 20 }}>Carregando indicadores…</div>;
  if (erro) return <div style={{ color: '#c62828', marginBottom: 20 }}>{erro}</div>;
  if (!dados) return null;

  const { ocupacao, evasao, financeiro } = dados;

  return (
    <section style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <Cartao
          titulo="Ocupação"
          valor={ocupacao.taxaPct === null ? '—' : `${ocupacao.taxaPct}%`}
          cor={ocupacao.taxaPct === null ? '#999' : corOcupacao(ocupacao.taxaPct)}
          nota={ocupacao.taxaPct === null
            ? 'Nenhuma turma com capacidade cadastrada'
            : `${ocupacao.matriculadasTotal} matriculadas · ${ocupacao.capacidadeTotal} vagas`}
        />
        <Cartao
          titulo="Evasão no mês"
          valor={evasao.taxaPct === null ? '—' : `${evasao.taxaPct}%`}
          cor={evasao.taxaPct === null ? '#999' : corEvasao(evasao.taxaPct)}
          nota={`${evasao.saidas} saída(s) — ${evasao.cancelamentos} cancelada(s), ${evasao.trancamentos} trancada(s)`}
        />
        <Cartao
          titulo="Inadimplência"
          valor={`${Number(financeiro?.inadimplenciaPct ?? 0)}%`}
          cor={Number(financeiro?.inadimplenciaPct ?? 0) <= 5 ? '#2e7d32' : Number(financeiro?.inadimplenciaPct ?? 0) <= 15 ? '#e65100' : '#c62828'}
          nota={`${brl(financeiro?.vencidoValor)} vencido em ${financeiro?.vencidoQtd ?? 0} boleto(s)`}
        />
        <Cartao
          titulo="Recebido no mês"
          valor={brl(financeiro?.recebidoMes)}
          cor="#2e7d32"
          nota={`${brl(financeiro?.aReceber)} a receber`}
        />
      </div>

      {/* Ocupação por turma — é onde a decisão de matricular acontece. */}
      {ocupacao.turmas.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 14 }}>
          <h4 style={{ margin: '0 0 10px' }}>Ocupação por turma</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ocupacao.turmas.map((t) => (
              <div key={t.id_turma} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 160px', fontWeight: 600 }}>
                  {t.turma}{t.excedida && <span style={{ color: '#c62828', marginLeft: 6 }}>⚠️ acima da capacidade</span>}
                </span>
                <span style={{ fontSize: 13, color: '#666', minWidth: 96 }}>
                  {t.matriculadas}{t.capacidade !== null ? ` / ${t.capacidade}` : ''}
                </span>
                <div style={{ flex: '2 1 220px', background: '#f2f2f2', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                  {t.taxaPct !== null && (
                    <div style={{ width: `${Math.min(t.taxaPct, 100)}%`, height: '100%', background: corOcupacao(t.taxaPct) }} />
                  )}
                </div>
                <span style={{ minWidth: 56, textAlign: 'right', fontWeight: 700, color: t.taxaPct === null ? '#999' : corOcupacao(t.taxaPct) }}>
                  {t.taxaPct === null ? '—' : `${t.taxaPct}%`}
                </span>
              </div>
            ))}
          </div>

          {/* Dizer o que NÃO foi contado, em vez de entregar um número redondo
              que esconde a lacuna. */}
          {ocupacao.semTurmaValida > 0 && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: '#8a2c00', background: '#fff1e8', border: '1px solid #ffc9a3', borderRadius: 8, padding: '8px 10px' }}>
              ⚠️ <strong>{ocupacao.semTurmaValida} matrícula(s) ativa(s) sem turma válida</strong> — a turma foi
              excluída ou nunca foi atribuída. Não ocupam vaga em nenhuma linha acima e precisam de enturmação.
            </div>
          )}

          {ocupacao.semCapacidade.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: '#8a6d00', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 8, padding: '8px 10px' }}>
              ⚠️ Fora do cálculo por não terem capacidade cadastrada: <strong>{ocupacao.semCapacidade.join(', ')}</strong>.
              Informe a capacidade no cadastro da turma para que entrem na taxa.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
